import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faCheckCircle, faExclamationCircle } from '@fortawesome/pro-light-svg-icons'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import useCurrentUser from '@hooks/useCurrentUser'
import useOrderItemTask, { generateUrls } from '@hooks/useOrderItemTask'
import useOrderItemTaskTypes from '@hooks/useOrderItemTaskTypes'
import useOrderItemTasks from '@hooks/useOrderItemTasks'
import useUser from '@hooks/useUser'

import type { OrderItemTaskFilters } from '@hooks/useOrderItemTasks'

import { groupOrderItemTasks } from '@functions/orderItemTask'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import type { OrderItemTaskTypeModel } from '@models/types'

import OrderItemTaskGroup from '../OrderItemTaskGroup'

const buildPageFilterFields = (orderItemTaskTypes: OrderItemTaskTypeModel[]) => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Order, Address or Suburb',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      key: 'order_item_task_type_id',
      label: 'Task Type',
      values: orderItemTaskTypes.map(orderItemTaskType => ({
        key: orderItemTaskType.id,
        label: orderItemTaskType.title,
      })),
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'users',
        entityTitleKey: 'full_name',
      },
      key: 'user_id',
    },
  ]
}

type QueryParams = { status_key?: 'complete' | 'current'}

interface PageContentProps {
  pageFilters: OrderItemTaskFilters,
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const { status_key }: QueryParams = parsePermittedQueryParams(getQueryParamsFromHash(), ['status_key'])
  const statusKey = status_key || 'current'

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  // @ts-expect-error TS2339
  const { user } = useUser({ id: pageFilters.user_id })

  const orderItemTasksPayload = useOrderItemTasks({
    filters: {
      completion_status_key: statusKey,
      provider_employee_id: user.actable_id,
      ...pageFilters,
    },
    performHttpRequests: true,
    requestOptions: {
      include_task_instruction_image: true,
      include_task_order_item: true,
      // @ts-expect-error TS2353
      include_task_provider_employee: true,
    },
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filteredOrderItemTasks,
    filteredOrderItemTasksCount,
    hasFilteredOrderItemTasks,
    loading,
  } = orderItemTasksPayload

  const orderItemTasksByGroup = groupOrderItemTasks({
    orderItemTasks: filteredOrderItemTasks,
    filterCompletionStatusKey: statusKey,
  })

  const { provider } = useCurrentUser()

  const orderItemTaskPayload = useOrderItemTask({ provider_id: provider?.id })
  const {
    callbacks: {
      createOrEditOrderItemTask,
      updateSortOrder,
    },
  } = orderItemTaskPayload

  const { filteredOrderItemTaskTypes } = useOrderItemTaskTypes({ performHttpRequests: true })

  const { completeOrderItemTasksUrl, currentOrderItemTasksUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Tasks | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => createOrEditOrderItemTask()} size="medium">
            Create
          </Button>
        )}
        activeTabBarItemKey={statusKey}
        tabBarItems={[
          {
            href: currentOrderItemTasksUrl,
            icon: faCheckCircle,
            key: 'current',
            title: 'Current',
          },
          {
            href: completeOrderItemTasksUrl,
            icon: faExclamationCircle,
            key: 'complete',
            title: 'Complete',
          },
        ]}
        title="Tasks"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredOrderItemTasks && <ListBlankState />}

            {!loading && hasFilteredOrderItemTasks && Object.values(orderItemTasksByGroup).map(group => (
              <OrderItemTaskGroup
                // @ts-expect-error TS2322
                callbacks={{ updateSortOrder }}
                key={group.groupKey}
                groupDate={group.groupKey}
                orderItemTaskIds={group.orderItemTaskIds}
              />
            ))}

            {!loading && (
              <Box variant="white">
                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredOrderItemTasksCount}
                  loading={loading}
                  showBorderTop={false}
                />
              </Box>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields(filteredOrderItemTaskTypes)}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
